import React, { useState, useEffect } from 'react';
import { useParams, useNavigate, useLocation } from 'react-router-dom';
import { Box, Spinner, Text, Button, Flex, Heading, Radio, RadioGroup, Stack, Image, useColorMode } from "@chakra-ui/react";
import OfficeInfo from './OfficeInfo';
import FullScreenMessage from "./FullScreenMessage";
import { cqAPI } from "../../../lib/Auth";

const renderProviders = (dayInfo) => {
  const providerElements = [];
  for (let i = 0; dayInfo[`provider${i}_name`]; i++) {
    providerElements.push(
      <Flex className="provider-info" key={i} mb={4} alignItems="center">
        <Image src={dayInfo[`provider${i}_img`]} alt={dayInfo[`provider${i}_name`]} boxSize="50px" borderRadius="full" mr={4} />
        <Box>
          <Heading as="h4" size="sm">{dayInfo[`provider${i}_name`]}</Heading>
          <Text>{dayInfo[`provider${i}_desc`]}</Text>
        </Box>
      </Flex>
    );
  }
  return providerElements;
};

const Availabilities = () => {
  const { serviceId } = useParams();
  const [hasError, setHasError] = useState(false);
  const navigate = useNavigate();
  const location = useLocation();
  const [availability, setAvailability] = useState([]);
  const [selectedOption, setSelectedOption] = useState('');
  const [service, setService] = useState({});
  const [officeData, setOfficeData] = useState({});
  const [isLoading, setIsLoading] = useState(true);
  const { colorMode } = useColorMode();

  useEffect(() => {
    setIsLoading(true);
    const params = new URLSearchParams(window.location.search);

    const providerId = params.get("provider_id");
    const appId = params.get("app_id");
    const from = params.get("from");
    const days = params.get("days");

    const qs = new URLSearchParams({
      service_id: String(serviceId),
      timepref: String(selectedOption),
      ...(providerId ? { pms_provider_id: providerId } : {}),
      ...(appId ? { pms_app_id: appId } : {}),
      ...(from ? { from } : {}),
      ...(days ? { days } : {}),
    }).toString();

    cqAPI.get(`/times?${qs}`)
    .then(data => {
        if (data.type === 'ok') {
          setAvailability(data.data.availabilities);
          setService(data.data.service);
          setOfficeData(data.data.business);
        } else {
          if (data.code) {
            setHasError(true);
          }
        }
        setIsLoading(false);
      })
      .catch(error => {
        setHasError(true);
        setIsLoading(false);
      });
  }, [serviceId, selectedOption]);

  const handleOptionChange = (value) => {
    setIsLoading(true);
    setSelectedOption(value);
  };

  const handleDayClick = (date) => {
    const formattedDate = new Date(date).toLocaleDateString('en-US', {
      month: '2-digit',
      day: '2-digit',
      year: 'numeric'
    }).replace(/\//g, '-');

    const searchParams = new URLSearchParams(location.search);
    searchParams.set('timepref', selectedOption);

    navigate({
      pathname: `/appointments/new/service/${serviceId}/${formattedDate}`,
      search: `?${searchParams.toString()}`
    });
  };

  const changeService = () => {
    const searchParams = new URLSearchParams(location.search);
    searchParams.delete('timepref');

    navigate({
      pathname: `/appointments/new`,
      search: `?${searchParams.toString()}`
    });
  };

  if (hasError) {
    return <FullScreenMessage message="This is not a valid link" />;
  }

  if (isLoading) {
    return (
      <Flex justifyContent="center" alignItems="center" height="100vh">
        <Spinner size="xl" />
      </Flex>
    );
  }

  return (
    <Box padding="40px" bg={colorMode === "light" ? "white" : "gray.800"} borderRadius="md" boxShadow="md" className="availability-container" id="availability-block">
      <Box className="service-header" display="flex" justifyContent="space-between" alignItems="center" mb={4}>
        <Text className="selected-service" fontWeight="bold" id={`${service.name}-service`}>{service.name}</Text>
        <Button className="change-service" id="change-service" onClick={changeService} cursor="pointer">Change service</Button>
      </Box>
      <Box className="appointment-selector" mb={4}>
        <Heading as="h2" size="lg" mb={4}>When would you like to come in?</Heading>
        <RadioGroup onChange={handleOptionChange} value={selectedOption} id="selector-radio">
          <Stack direction="row" spacing={5}>
            <Radio value="">Earliest</Radio>
            <Radio value="morning">Morning</Radio>
            <Radio value="afternoon">Afternoon</Radio>
          </Stack>
        </RadioGroup>
      </Box>
      {availability.map((dayInfo, index) => (
        <Box
          key={index}
          className="day-block actionableItem"
          onClick={() => handleDayClick(dayInfo.date)}
          border="1px"
          borderColor="gray.200"
          borderRadius="md"
          p={4}
          mb={4}
          cursor="pointer"
          _hover={{ bg: "gray.100" }}
        >
          <Box className="date-header" mb={4}>
            <Heading as="h3" size="md">{`${dayInfo.day}, ${new Date(dayInfo.date).toLocaleDateString()}`}</Heading>
          </Box>
          {renderProviders(dayInfo)}
        </Box>
      ))}
    </Box>
  );
};

export default Availabilities;
